/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: AiAgentCallsPage.tsx
 🗓️ Created: 2026-01-17
 🧩 Description:
    Call history page for a specific AI Agent.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { RefreshCw, Eye, Phone, Clock, X, Search, Coins } from 'lucide-react';

import { useNotifications } from '../../contexts/NotificationContext';
import { useI18n } from '../../hooks/useI18n';
import { ModuleFormHeader } from '../../components/layouts';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { getAiAgentById, getAiAgentCalls, getCallTranscripts, type AiAgentCall, type AiAgentTranscript, type AiAgent } from './api/aiAgentsService';

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

export default function AiAgentCallsPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useI18n();
    const { showError } = useNotifications();

    const [agent, setAgent] = useState<AiAgent | null>(null);
    const [calls, setCalls] = useState<AiAgentCall[]>([]);
    const [filteredCalls, setFilteredCalls] = useState<AiAgentCall[]>([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(25);
    const [searchQuery, setSearchQuery] = useState('');

    const [transcriptModal, setTranscriptModal] = useState<{
        open: boolean;
        call: AiAgentCall | null;
        transcripts: AiAgentTranscript[];
        loading: boolean;
    }>({ open: false, call: null, transcripts: [], loading: false });

    const agentId = id || null;

    const fetchAgent = useCallback(async () => {
        if (!agentId) return;
        try {
            const data = await getAiAgentById(agentId);
            setAgent(data);
        } catch (error) {
            showError(t('aiAgents.messages.loadError') || 'Failed to load agent');
        }
    }, [agentId, showError, t]);

    const fetchCalls = useCallback(async () => {
        if (!agentId) return;
        setLoading(true);
        try {
            const response = await getAiAgentCalls(agentId, { page, limit, sortBy: 'start_time', sortOrder: 'desc' });
            setCalls(response.data || []);
            setFilteredCalls(response.data || []);
            setTotal(response.meta?.total || 0);
        } catch (error) {
            showError(t('aiAgents.messages.loadCallsError') || 'Failed to load calls');
        } finally {
            setLoading(false);
        }
    }, [agentId, page, limit, showError, t]);

    useEffect(() => {
        fetchAgent();
        fetchCalls();
    }, [fetchAgent, fetchCalls]);

    // Filter calls based on search query
    useEffect(() => {
        if (!searchQuery.trim()) {
            setFilteredCalls(calls);
        } else {
            const query = searchQuery.toLowerCase();
            setFilteredCalls(calls.filter(call =>
                call.callerIdNumber?.toLowerCase().includes(query) ||
                call.callerIdName?.toLowerCase().includes(query) ||
                call.status?.toLowerCase().includes(query)
            ));
        }
    }, [searchQuery, calls]);

    const handleViewTranscript = async (call: AiAgentCall) => {
        setTranscriptModal({ open: true, call, transcripts: [], loading: true });
        try {
            const transcripts = await getCallTranscripts(call.id);
            setTranscriptModal(prev => ({ ...prev, transcripts: transcripts || [], loading: false }));
        } catch (error) {
            showError(t('aiAgents.messages.loadTranscriptsError') || 'Failed to load transcripts');
            setTranscriptModal(prev => ({ ...prev, loading: false }));
        }
    };

    const handleLimitChange = (newLimit: number) => {
        setLimit(newLimit);
        setPage(1);
    };

    const formatDuration = (seconds: number | null) => {
        if (!seconds) return '-';
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleString();
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
            case 'active': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
            case 'failed': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
            default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
        }
    };

    const headerTitle = agent
        ? `${t('aiAgents.callHistory.title') || 'Call History'} - ${agent.agentName}`
        : (t('aiAgents.callHistory.title') || 'Call History');

    const totalPages = Math.ceil(total / limit);

    return (
        <div className="p-6">
            <ModuleFormHeader
                icon={<Phone className="h-5 w-5 text-primary" />}
                title={headerTitle}
                backButtonText={t('aiAgents.actions.list') || 'List'}
                onBack={() => navigate('/admin/ai-integration/agents')}
            >
                <div className="flex items-center gap-3">
                    {/* Search */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder={t('common.search') || 'Search...'}
                            className="pl-9 pr-4 py-2 w-64 bg-muted/50 border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                        />
                    </div>

                    {/* Items per page */}
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">{t('common.show') || 'Show'}:</span>
                        <select
                            value={limit}
                            onChange={(e) => handleLimitChange(Number(e.target.value))}
                            className="bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                        >
                            {ITEMS_PER_PAGE_OPTIONS.map(option => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                    </div>

                    {/* Total count */}
                    <span className="px-3 py-1 bg-muted rounded-full text-sm text-muted-foreground">
                        {total} {t('aiAgents.callHistory.totalCalls') || 'total calls'}
                    </span>

                    {/* Refresh */}
                    <SimpleTooltip content={t('common.refresh') || 'Refresh'} position="top">
                        <button
                            onClick={fetchCalls}
                            disabled={loading}
                            className="flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg transition-colors disabled:opacity-50"
                        >
                            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                            <span className="hidden sm:inline">{t('common.refresh') || 'Refresh'}</span>
                        </button>
                    </SimpleTooltip>
                </div>
            </ModuleFormHeader>

            <div className="mt-8">
                <div className="bg-card border border-border rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-border">
                        <thead className="bg-muted/50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('aiAgents.callHistory.startTime') || 'Start Time'}</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('aiAgents.callHistory.caller') || 'Caller'}</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('aiAgents.callHistory.duration') || 'Duration'}</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('aiAgents.callHistory.status') || 'Status'}</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('aiAgents.callHistory.bargeIns') || 'Barge-Ins'}</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('aiAgents.callHistory.turns') || 'Turns'}</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('aiAgents.callHistory.tokens') || 'Tokens'}</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('common.actions') || 'Actions'}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {loading ? (
                                <tr>
                                    <td colSpan={8} className="px-6 py-12 text-center">
                                        <div className="flex justify-center">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredCalls.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="px-6 py-12 text-center text-muted-foreground">
                                        {searchQuery ? (t('common.noSearchResults') || 'No results found') : (t('aiAgents.callHistory.noCalls') || 'No calls found')}
                                    </td>
                                </tr>
                            ) : (
                                filteredCalls.map((call, index) => (
                                    <tr key={call.id} className={`transition-colors ${index % 2 === 0 ? 'bg-card hover:bg-muted/50' : 'bg-muted/30 hover:bg-muted/60'}`}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">{formatDate(call.startTime)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                                            {call.callerIdNumber || '-'}
                                            {call.callerIdName && <span className="text-muted-foreground ml-1">({call.callerIdName})</span>}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">{formatDuration(call.durationSeconds)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(call.status)}`}>
                                                {call.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground text-center">{call.totalBargeIns}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                                            <span className="text-blue-600 dark:text-blue-400">AI: {call.totalAiTurns}</span>
                                            <span className="mx-1 text-muted-foreground">/</span>
                                            <span className="text-purple-600 dark:text-purple-400">C: {call.totalCallerTurns}</span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                            <SimpleTooltip
                                                content={`Text In: ${(call.tokensInputText || 0).toLocaleString()}\nText Out: ${(call.tokensOutputText || 0).toLocaleString()}\nAudio In: ${(call.tokensInputAudio || 0).toLocaleString()}\nAudio Out: ${(call.tokensOutputAudio || 0).toLocaleString()}\nCached: ${((call.tokensCachedText || 0) + (call.tokensCachedAudio || 0)).toLocaleString()}\nSTT: ${(call.tokensTranscription || 0).toLocaleString()}`}
                                                position="top"
                                            >
                                                <span className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground">
                                                    <Coins className="h-3 w-3" />
                                                    {(call.tokensTotal || 0).toLocaleString()}
                                                </span>
                                            </SimpleTooltip>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            <SimpleTooltip content={t('aiAgents.callHistory.viewTranscript') || 'View Transcript'} position="top">
                                                <button
                                                    onClick={() => handleViewTranscript(call)}
                                                    className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
                                                >
                                                    <Eye className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                                </button>
                                            </SimpleTooltip>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-4 px-4">
                        <div className="text-sm text-muted-foreground">
                            {t('common.page') || 'Page'} {page} {t('common.of') || 'of'} {totalPages}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className="px-3 py-1 bg-muted hover:bg-muted/80 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {t('common.previous') || 'Previous'}
                            </button>
                            <button
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                                className="px-3 py-1 bg-muted hover:bg-muted/80 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {t('common.next') || 'Next'}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Transcript Modal */}
            {transcriptModal.open && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-card border border-border rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
                        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-semibold text-foreground">{t('aiAgents.callHistory.transcript') || 'Transcript'}</h2>
                                {transcriptModal.call && (
                                    <p className="text-sm text-muted-foreground">
                                        {formatDate(transcriptModal.call.startTime)} - {transcriptModal.call.callerIdNumber || 'Unknown'}
                                    </p>
                                )}
                            </div>
                            <button
                                onClick={() => setTranscriptModal(prev => ({ ...prev, open: false }))}
                                className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto max-h-96">
                            {transcriptModal.loading ? (
                                <div className="flex justify-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                </div>
                            ) : transcriptModal.transcripts.length === 0 ? (
                                <p className="text-center text-muted-foreground py-8">{t('aiAgents.callHistory.noTranscripts') || 'No transcripts available'}</p>
                            ) : (
                                <div className="space-y-4">
                                    {transcriptModal.transcripts.map((transcript, idx) => (
                                        <div
                                            key={idx}
                                            className={`p-4 rounded-lg border-l-4 ${transcript.speaker === 'ai'
                                                ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500'
                                                : 'bg-gray-50 dark:bg-gray-800/50 border-gray-400 dark:border-gray-600'
                                                }`}
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <span className={`text-xs font-medium px-2 py-1 rounded ${transcript.speaker === 'ai'
                                                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300'
                                                    : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                                                    }`}>
                                                    {transcript.speaker === 'ai' ? 'AI' : 'Caller'}
                                                </span>
                                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                    <Clock className="h-3 w-3" />
                                                    {new Date(transcript.timestamp).toLocaleTimeString()}
                                                    {transcript.interrupted && (
                                                        <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 rounded">
                                                            Interrupted
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <p className="text-sm text-foreground">{transcript.content}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="px-6 py-4 border-t border-border">
                            <button
                                onClick={() => setTranscriptModal(prev => ({ ...prev, open: false }))}
                                className="w-full px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg text-foreground transition-colors"
                            >
                                {t('common.close') || 'Close'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
